import React, { useMemo, useState, useEffect } from "react";
import { useFetchEvents } from "../../hooks/useQuery";
import { Spinner } from "react-bootstrap";
import sButtons from "../../assets/styles/buttons.module.css";
import styles from "./Events.module.css";
import { EventModalCreate } from "./EventModal/EventModalCreate";
import { useFetchRolesV2 } from "../../hooks/useQuery";
import { EventItem } from "./EventItem/EventItem";
import { LoadingErrorBlock } from "../shared/LoadingErrorBlock";
import { EventType } from "../../services/models/event";
import { classNames } from "../../Utils/classNames/classNames";
import { Accordion } from "../shared/Accordion/Accordion";
import { DropdownIndicator } from "../shared/DropdownIndicator";
import { IoChevronDownOutline } from "react-icons/io5";

interface EventsWrapperProps {
    isAdministrator: boolean;
}

export const EventsWrapper: React.FC<EventsWrapperProps> = ({ isAdministrator }) => {
    const { eventsData, eventsIsLoading, eventsError } = useFetchEvents();
    const { rolesData, rolesIsLoading, rolesError } = useFetchRolesV2(isAdministrator);
    const [visibleCount, setVisibleCount] = useState(4);
    const [modalShow, setModalShow] = useState(false);
    const [eventType, setEventType] = useState<EventType>("event");
    const [selectedEventTag, setSelectedEventTag] = useState<string>();

    const filtredEventsData = useMemo(() => {
        if (selectedEventTag) {
            return eventsData?.filter((event) => event.eventTags.includes(selectedEventTag)) || [];
        }
        return eventsData;
    }, [eventsData, selectedEventTag]);

    const memoizedEventsData = useMemo(
        () => filtredEventsData?.filter((event) => event.eventType === eventType) || [],
        [eventType, filtredEventsData],
    );

    const allEventTags = useMemo(() => {
        const tags = memoizedEventsData
            .flatMap((event) => event.eventTags)
            .filter((tag, index, self) => tag && self.indexOf(tag) === index);
        return tags;
    }, [memoizedEventsData]);

    useEffect(() => {
        if (memoizedEventsData?.length) {
            const currentlyVisible = Math.ceil(visibleCount / 4) * 4;
            if (memoizedEventsData.length <= currentlyVisible) {
                setVisibleCount(currentlyVisible);
            }
        }
    }, [memoizedEventsData, visibleCount]);

    const isLoading = eventsIsLoading || rolesIsLoading;
    const isError = !!(eventsError || rolesError);

    const handleShowMore = () => {
        setVisibleCount((prev) => Math.min(prev + 4, memoizedEventsData?.length || 0));
    };
    const switchTypeHandler = (type: EventType) => () => {
        setEventType(type);
    };

    return (
        <div className={styles.blockWrapper}>
            <LoadingErrorBlock
                isLoading={isLoading}
                isError={isError}
                errorMessage="Ошибка загрузки событий. Попробуйте обновить страницу"
            />
            <div className="d-flex flex-row justify-content-between align-items-center h-100">
                <div className="d-flex w-100">
                    <div
                        className={classNames(styles.eventType, { [styles.active]: eventType === "event" }, [])}
                        onClick={switchTypeHandler("event")}
                    >
                        События
                    </div>
                    <div
                        className={classNames(styles.eventTypeDpo, { [styles.active]: eventType === "dpo" }, [])}
                        onClick={switchTypeHandler("dpo")}
                    >
                        ДПО
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-end align-items-center w-100">
                    <Accordion
                        items={allEventTags}
                        label="Отфильтровать по"
                        clear
                        addonRight={<IoChevronDownOutline size={24} color={"#363636"} />}
                        className="w-50"
                        selectItem={setSelectedEventTag}
                    />
                    {isAdministrator && (
                        <button
                            onClick={() => setModalShow(true)}
                            className={`${sButtons.iconButton} ${sButtons.addButton}`}
                        >
                            Добавить событие
                        </button>
                    )}
                </div>
            </div>
            {memoizedEventsData && memoizedEventsData.length > 0 ? (
                <>
                    <div className={styles.eventsWrapper}>
                        {memoizedEventsData.slice(0, visibleCount).map((event) => (
                            <EventItem
                                key={event._id}
                                roles={rolesData}
                                item={event}
                                isAdministrator={isAdministrator}
                            />
                        ))}
                    </div>
                    {visibleCount < memoizedEventsData.length && (
                        <div className="d-flex justify-content-center ">
                            <button
                                onClick={handleShowMore}
                                className={`${sButtons.iconButton} ${sButtons.lightBlueButton}`}
                            >
                                Показать еще события
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div className="d-flex justify-content-center align-items-center h-100">
                    <p>У Вас нет актуальных событий</p>
                </div>
            )}
            {isAdministrator && (
                <EventModalCreate roles={rolesData || []} show={modalShow} onHide={() => setModalShow(false)} />
            )}
        </div>
    );
};
